import axios, { AxiosError } from 'axios';
import { tokenStorage } from './tokenStorage';

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'http://localhost:8000/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 20000,
});

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** A user-facing message extracted from an API error, safe to render directly. */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (axios.isAxiosError(error)) {
    const err = error as AxiosError<{ message?: string; detail?: string | { msg?: string }[] }>;
    if (!err.response) {
      return 'Unable to reach the server. Please check your connection and try again.';
    }
    const data = err.response.data;
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data?.detail) && data.detail[0]?.msg) return (data.detail[0].msg as string).replace(/^Value error, /, '');
    if (data?.message) return data.message;
    if (err.response.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.response.status === 403) return 'You do not have permission to perform this action.';
    if (err.response.status === 404) return 'The requested record could not be found.';
    if (err.response.status >= 500) return 'The server ran into a problem. Please try again shortly.';
  }
  return fallback;
}

let unauthorizedHandler: (() => void) | null = null;
export function registerUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      tokenStorage.clear();
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  }
);

export default apiClient;
